import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadProps } from '../admin/types';
import { Tabs, Tab } from '@heroui/react';
import FileUpload from './file-upload/FileUpload';
import ExternalUrlUpload, { ExternalUrlUploadProps } from './file-upload/ExternalUrlUpload';
import MediaLibraryContainer, { MediaLibraryContainerProps } from '@/containers/media-library/MediaLibraryContainer';
import { AcceptedFileType, FileUploadTabType, isFileUploadType } from './file-upload/utils';

type UploadMode = 'file' | 'url' | 'library';

type TabsComponentsMap = Record<
  FileUploadTabType,
  {
    readonly type: FileUploadTabType;
    readonly Component: React.ComponentType<any>;
    readonly getProps: (
      props: FileUploadProps,
    ) => FileUploadProps | ExternalUrlUploadProps | MediaLibraryContainerProps;
  }
>;

const tabsComponentsMap: TabsComponentsMap = {
  [FileUploadTabType.FILE_UPLOAD]: {
    type: FileUploadTabType.FILE_UPLOAD,
    Component: FileUpload,
    getProps: (props: FileUploadProps) => ({
      file: props.file,
      onChange: props.onChange,
      acceptedTypes: props.acceptedTypes,
      maxSize: props.maxSize,
      isMediaLibrary: props.isMediaLibrary,
      onFileUploaded: props.onFileUploaded,
    }),
  },
  [FileUploadTabType.EXTERNAL_URL]: {
    type: FileUploadTabType.EXTERNAL_URL,
    Component: ExternalUrlUpload,
    getProps: (props: FileUploadProps) => ({
      acceptedTypes: props.acceptedTypes,
      isMediaLibrary: props.isMediaLibrary,
      onFileUploaded: props.onFileUploaded,
    }),
  },
  [FileUploadTabType.MEDIA_LIBRARY]: {
    type: FileUploadTabType.MEDIA_LIBRARY,
    Component: MediaLibraryContainer,
    getProps: (props: FileUploadProps) => ({
      onFileSelect: props.onFileUploaded,
      acceptedTypes: props.acceptedTypes,
      selectedFileId: props.selectedFileId,
    }),
  },
};

const FileUploadField: React.FC<FileUploadProps> = ({
  file,
  onChange,
  acceptedTypes = [AcceptedFileType.PDF],
  maxSize = 30,
  shownTabs = [FileUploadTabType.FILE_UPLOAD],
  onFileUploaded,
  selectedFileId,
  isMediaLibrary = false,
}) => {
  const { t } = useTranslation('admin');
  const [mode, setMode] = useState<UploadMode>('file');

  // Check if only YouTube is accepted (no file upload types)
  const hasFileUploadTypes = acceptedTypes.some((type) => isFileUploadType(type));
  const hasOnlyYouTube = acceptedTypes.length === 1 && acceptedTypes[0] === AcceptedFileType.YOUTUBE;
  const shouldShowOnlyExternalUrl = hasOnlyYouTube || !hasFileUploadTypes;
  const tabs = shouldShowOnlyExternalUrl ? [FileUploadTabType.EXTERNAL_URL] : shownTabs;
  const handleModeChange = (key: string | number) => {
    setMode(key as UploadMode);
  };

  const handleFileSelect = (fileMetadata: Parameters<NonNullable<typeof onFileUploaded>>[0]) => {
    if (onFileUploaded) {
      onFileUploaded(fileMetadata);
    }
  };

  // Filter out YouTube from acceptedTypes for MediaLibrary (it only shows S3 files)
  const mediaLibraryAcceptedTypes = useMemo(() => {
    return acceptedTypes.filter((type) => isFileUploadType(type));
  }, [acceptedTypes]);

  // If only one tab, don't show tabs
  const singleTab = tabs.length === 1 ? tabsComponentsMap[tabs[0]] : null;
  if (singleTab) {
    return (
      <singleTab.Component
        {...singleTab.getProps({
          file,
          onChange,
          acceptedTypes: singleTab.type === FileUploadTabType.MEDIA_LIBRARY ? mediaLibraryAcceptedTypes : acceptedTypes,
          onFileUploaded: singleTab.type === FileUploadTabType.MEDIA_LIBRARY ? handleFileSelect : onFileUploaded,
          maxSize,
          isMediaLibrary,
          selectedFileId: singleTab.type === FileUploadTabType.MEDIA_LIBRARY ? selectedFileId : undefined,
        })}
      />
    );
  }

  // Multiple tabs - show tabs UI
  return (
    <div className="space-y-2">
      <Tabs selectedKey={mode} onSelectionChange={handleModeChange} variant="bordered" className="w-full">
        {Object.values(tabsComponentsMap)
          .filter((tab) => shownTabs.includes(tab.type))
          .map((tab) => (
            <Tab key={tab.type} title={t(`fileUpload.tabs.${tab.type}`, { defaultValue: tab.type })}>
              <tab.Component
                {...tab.getProps({
                  file,
                  onChange,
                  acceptedTypes:
                    tab.type === FileUploadTabType.MEDIA_LIBRARY ? mediaLibraryAcceptedTypes : acceptedTypes,
                  onFileUploaded: tab.type === FileUploadTabType.MEDIA_LIBRARY ? handleFileSelect : onFileUploaded,
                  maxSize,
                  isMediaLibrary,
                  selectedFileId: tab.type === FileUploadTabType.MEDIA_LIBRARY ? selectedFileId : undefined,
                })}
              />
            </Tab>
          ))}
      </Tabs>
    </div>
  );
};

export default FileUploadField;
